import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './PrivacyPolicy.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Account Deletion', path: '/account-deletion' },
];

const sections = [
  {
    id: 'overview',
    badge: '01',
    title: 'Overview',
    body: `Baatcheet is dedicated to safeguarding user privacy and ensuring that every user has complete control over their personal data. In accordance with Google Play's User Data policy and in support of individual user rights, we have established a straightforward and accessible process for account deletion.`,
  },
  {
    id: 'user-deletion',
    badge: '02',
    title: 'User-Initiated Account Deletion',
    body: `Every user holds the right to delete their Baatcheet account at any point in time, directly through the app.

**How to Delete Your Account**

— To begin the deletion process, go to the Profile section within the app and tap the "Delete Account" option.
— Alternatively, you may reach out to our support team by sending an email to baatcheet.connect@gmail.com to request account deletion.
— Once your request is received, our team will review it and get back to you within 7 days with a confirmation or further steps.

**What Happens After Your Account is Deleted**

— All personal data, profile details, and activity history will be permanently removed from our active systems within 30 days of the deletion request.
— Account deletion is final and cannot be undone under any circumstances.
— Any in-app purchases or active subscriptions are non-refundable following account deletion.

This process ensures that every user retains full authority over their presence on the platform, in line with Google Play's requirements.`,
  },
  {
    id: 'platform-deletion',
    badge: '03',
    title: 'Baatcheet-Initiated Account Deletion',
    body: `We reserve the right to suspend or permanently remove any account found to be in violation of our Terms of Use or Community Guidelines. Violations include, but are not limited to:

— Harassment, hate speech, or abusive conduct toward other users
— Using the app for fraudulent activity, impersonation, or any unlawful purpose
— Spamming or exploiting app features in an unauthorized manner
— Recording or sharing conversations without the explicit consent of all parties involved

**Enforcement Process**

— A user may first receive a warning or face a temporary suspension.
— Our moderation team will conduct a thorough review and investigation of the reported violation.
— If the violation is confirmed, the account may be permanently deleted from the platform.
— Users may appeal the decision within 7 days of being notified by writing to baatcheet.connect@gmail.com.`,
  },
  {
    id: 'data-retention',
    badge: '04',
    title: 'Data Retention & Legal Exceptions',
    body: `Following account deletion:

— All user data is removed from our active systems, unless retention is mandated by applicable law.
— A minimal amount of metadata may be preserved for the purposes of fraud prevention or dispute resolution, as permitted under relevant regulations.
— No call recordings are retained unless a call has been explicitly reported by a user.`,
  },
  {
    id: 'contact',
    badge: '05',
    title: 'Contact Us',
    body: `For any assistance related to account deletion or for questions concerning your data and privacy, please reach out to us:

📧 Email: baatcheet.connect@gmail.com`,
  },
];

function AccountDeletion() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="How to Delete Your Baatcheet Account | Baatcheet"
        description="Step-by-step guide to deleting your Baatcheet account and understanding what happens to your data afterward."
        path="/account-deletion"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── Hero ── */}
        <section className="pp-hero shell reveal">
          <div className="pp-hero-tag">Legal Documents</div>
          <h1 className="pp-hero-title">
            Account<br />
            <span className="pp-hero-accent">Deletion</span>
          </h1>
          <p className="pp-hero-sub">
            You have full control over your Baatcheet account and data. This policy explains how to delete your account, what happens to your data, and how we handle enforcement.
          </p>
          <div className="pp-meta">
            <span className="pp-meta-item">Last Updated: May 2026</span>
            <span className="pp-meta-sep">·</span>
            <span className="pp-meta-item">Governed by Indian Law</span>
            <span className="pp-meta-sep">·</span>
            <a className="pp-meta-item pp-meta-link" href="mailto:baatcheet.connect@gmail.com?subject=Account%20Deletion%20Request">Request Deletion</a>
          </div>
        </section>

        {/* ── Table of Contents ── */}
        <section className="pp-toc-wrap shell reveal">
          <div className="pp-toc">
            <div className="pp-toc-label">Jump to section</div>
            <div className="pp-toc-grid">
              {sections.map(s => (
                <a key={s.id} className="pp-toc-item" href={`#${s.id}`}>
                  <span className="pp-toc-num">{s.badge}</span>
                  <span className="pp-toc-name">{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── Policy Sections ── */}
        <section className="pp-body shell">
          {sections.map((s, i) => (
            <article key={s.id} id={s.id} className={`pp-article reveal ${i % 2 === 1 ? 'pp-article--alt' : ''}`}>
              <div className="pp-article-num">{s.badge}</div>
              <div className="pp-article-content">
                <h2 className="pp-article-title">{s.title}</h2>
                <div className="pp-article-body">
                  {s.body.split('\n\n').map((para, pi) => {
                    if (para.startsWith('**') && para.endsWith('**') && para.indexOf('**', 2) === para.length - 2) {
                      return <h3 key={pi} className="pp-subhead">{para.slice(2, -2)}</h3>;
                    }
                    if (para.startsWith('— ')) {
                      const items = para.split('\n').filter(Boolean);
                      return (
                        <ul key={pi} className="pp-bullets">
                          {items.map((item, ii) => (
                            <li key={ii} dangerouslySetInnerHTML={{
                              __html: item.replace(/^— /, '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                            }} />
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={pi} dangerouslySetInnerHTML={{
                        __html: para.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      }} />
                    );
                  })}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* ── CTA strip ── */}
        <section className="pp-cta shell reveal">
          <div className="pp-cta-inner">
            <div className="pp-cta-text">
              <h3>Want to delete your account?</h3>
              <p>Email our support team and we will process your request within 7 business days.</p>
            </div>
            <a
              className="btn btn-cyan"
              href="mailto:baatcheet.connect@gmail.com?subject=Account%20Deletion%20Request"
            >
              ✉ Request Deletion
            </a>
          </div>
        </section>

        <div className="pp-related shell">
          <span>Related:</span>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/refund-policy">Refund Policy</Link>
        </div>

      </main>

      <Footer />
    </>
  );
}

export default AccountDeletion;
